import { useColorScheme, Platform } from 'react-native';
import { useMemo } from 'react';
import { getBrandingPrimaryColor } from '@/hooks/useBrandingOverrides';

export interface Theme {
  isDark: boolean;
  colors: {
    background: string;
    tabBarBackground: string;
    tabBarOverlay: string;
    tabBarBlurTint: 'light' | 'dark' | 'default';
    tabBarActiveTint: string;
    tabBarInactiveTint: string;
    shadow: string;
    primary: string;
    inputBackground: string;
    inputBorder: string;
    inputBorderFocused: string;
    inputText: string;
    placeholder: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    divider: string;
    cardBackground: string;
    error: string;
    errorAccent: string;
    success: string;
    successAccent: string;
    warning: string;
    warningAccent: string;
    socialButtonBackground: string;
    socialButtonBorder: string;
    socialButtonText: string;
  };
  blur: {
    intensity: number;
    overlayOpacity: number;
  };
}

const DEFAULT_PRIMARY = '#A855F7';

const getOverlayOpacityForPlatform = () => {
  if (Platform.OS === 'ios') return 0.05;
  if (Platform.OS === 'android') return 0.35;
  return 0.3;
};

const getTabBarBackgroundForPlatform = () => {
  if (Platform.OS === 'web') return 'rgba(18, 18, 18, 0.85)';
  return 'rgba(26, 26, 26, 0.3)';
};

const getBlurIntensityForPlatform = () => {
  if (Platform.OS === 'ios') return 100;
  if (Platform.OS === 'android') return 95;
  return 100;
};

export const darkTheme: Theme = {
  isDark: true,
  colors: {
    background: '#000000',
    tabBarBackground: getTabBarBackgroundForPlatform(),
    tabBarOverlay: `rgba(18, 18, 18, ${getOverlayOpacityForPlatform()})`,
    tabBarBlurTint: 'dark',
    tabBarActiveTint: DEFAULT_PRIMARY,
    tabBarInactiveTint: '#999999',
    shadow: 'rgba(0, 0, 0, 0.5)',
    primary: DEFAULT_PRIMARY,
    inputBackground: '#2a2a2a',
    inputBorder: '#333',
    inputBorderFocused: DEFAULT_PRIMARY,
    inputText: '#ffffff',
    placeholder: '#666',
    textPrimary: '#ffffff',
    textSecondary: '#888',
    textMuted: '#aaa',
    divider: '#333',
    cardBackground: '#1a1a1a',
    error: '#ff4444',
    errorAccent: '#cc0000',
    success: '#22c55e',
    successAccent: '#16a34a',
    warning: '#ff9800',
    warningAccent: '#f57c00',
    socialButtonBackground: '#1A1A1A',
    socialButtonBorder: '#333',
    socialButtonText: '#ffffff',
  },
  blur: {
    intensity: getBlurIntensityForPlatform(),
    overlayOpacity: getOverlayOpacityForPlatform(),
  },
};

export function useTheme(): Theme {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';
  const primary = getBrandingPrimaryColor() || DEFAULT_PRIMARY;

  return useMemo(() => ({
    isDark,
    colors: {
      background: isDark ? '#000000' : '#FFFFFF',
      tabBarBackground: getTabBarBackgroundForPlatform(),
      tabBarOverlay: `rgba(18, 18, 18, ${getOverlayOpacityForPlatform()})`,
      tabBarBlurTint: 'dark' as const,
      tabBarActiveTint: primary,
      tabBarInactiveTint: '#999999',
      shadow: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
      primary,
      inputBackground: isDark ? '#2a2a2a' : '#f5f5f5',
      inputBorder: isDark ? '#333' : '#ddd',
      inputBorderFocused: primary,
      inputText: isDark ? '#ffffff' : '#000',
      placeholder: isDark ? '#666' : '#999',
      textPrimary: isDark ? '#ffffff' : '#000',
      textSecondary: isDark ? '#888' : '#666',
      textMuted: isDark ? '#aaa' : '#777',
      divider: isDark ? '#333' : '#ddd',
      cardBackground: isDark ? '#1a1a1a' : '#ffffff',
      error: '#ff4444',
      errorAccent: '#cc0000',
      success: '#22c55e',
      successAccent: '#16a34a',
      warning: '#ff9800',
      warningAccent: '#f57c00',
      socialButtonBackground: isDark ? '#1A1A1A' : '#ffffff',
      socialButtonBorder: isDark ? '#333' : '#ddd',
      socialButtonText: isDark ? '#ffffff' : '#000',
    },
    blur: {
      intensity: getBlurIntensityForPlatform(),
      overlayOpacity: getOverlayOpacityForPlatform(),
    },
  }), [isDark, primary]);
}
